import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Download, MapPin, Briefcase, User, Copy, ReceiptText, Upload } from "lucide-react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import ChatPanel from "@/components/chat/ChatPanel";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RESPONSE_TYPES, responseTypeMeta } from "@/constants/vendorResponses";
import DocumentsPanel from "@/components/DocumentsPanel";
import { fmtDate, fmtDateTime } from "@/lib/utils";

const TAX_LOCKED = ["invoiced", "closed", "cancelled", "rejected"];
import SearchableSelect from "@/components/SearchableSelect";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const DELIVERY_STATUSES = [
  { value: "in_progress", label: "In Progress / Preparing" },
  { value: "delivered", label: "Delivered" },
];
const emptyForm = { lineRef: "", lineRefs: [], newPrice: "", proposedItem: "", estimatedDate: "", date: "", note: "", trackingNumber: "", carrier: "", shipDate: "" };

const emptyUpload = { invoiceNumber: "", total: "", invoiceDate: "", dueDate: "", payLink: "", notes: "", file: null };

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [po, setPo] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [showRespond, setShowRespond] = useState(false);
  const [respondType, setRespondType] = useState("acknowledged");
  const [form, setForm] = useState(emptyForm);
  const [showUpload, setShowUpload] = useState(false);
  const [upload, setUpload] = useState(emptyUpload);
  const { user } = useAuth();

  // Mark a line taxable / non-taxable — taxed at the job's rate (Test Drive 2026-10-21).
  const setLineTaxable = async (i, taxable) => {
    try {
      const { data } = await api.patch(`/vendor/orders/${id}/lines/${i}/taxable`, { taxable });
      setPo(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update the line");
    }
  };

  const load = () => {
    api.get(`/vendor/orders/${id}`).then((r) => setPo(r.data));
    api.get("/vendor/invoices").then((r) => setInvoices(r.data.filter((i) => i.po_id === id)));
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const createInvoice = async () => {
    try {
      const r = await api.post(`/vendor/orders/${id}/invoices`);
      toast.success(`Draft invoice ${r.data.invoice_number} created from this PO`);
      navigate(`/vendor/invoices/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create invoice");
    }
  };

  const submitUpload = async () => {
    if (!upload.invoiceNumber || !upload.total || !upload.file) return toast.error("Invoice number, total, and the file are required");
    const fd = new FormData();
    fd.append("invoice_number", upload.invoiceNumber);
    fd.append("total", upload.total);
    if (upload.invoiceDate) fd.append("invoice_date", upload.invoiceDate);
    if (upload.dueDate) fd.append("due_date", upload.dueDate);
    if (upload.payLink) fd.append("pay_link_url", upload.payLink);
    if (upload.notes) fd.append("notes", upload.notes);
    fd.append("file", upload.file);
    try {
      const r = await api.post(`/vendor/orders/${id}/invoices/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Invoice ${r.data.invoice_number} uploaded and sent`);
      setShowUpload(false);
      setUpload(emptyUpload);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    }
  };

  if (!po) return <div className="p-8 text-slate-400 text-sm">Loading…</div>;

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/vendor/orders/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) {
      toast.error("Failed to load PDF");
    }
  };

  const updateStatus = async (status) => {
    try {
      await api.patch(`/vendor/orders/${id}/delivery-status`, { delivery_status: status, status });
      toast.success("Status updated");
      load();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const openRespond = (type) => { setRespondType(type); setForm(emptyForm); setShowRespond(true); };

  const toggleLineRef = (idx) => {
    const s = String(idx);
    setForm((f) => ({ ...f, lineRefs: f.lineRefs.includes(s) ? f.lineRefs.filter((x) => x !== s) : [...f.lineRefs, s] }));
  };

  const submitRespond = async () => {
    let details = {};
    if (respondType === "backordered") {
      if (form.lineRefs.length === 0) return toast.error("Select at least one line item");
      details = { line_refs: form.lineRefs.map(Number), estimated_date: form.estimatedDate || null };
    } else if (respondType === "price_changed") {
      if (form.lineRef === "" || !form.newPrice) return toast.error("Select a line and enter the new price");
      details = { line_ref: Number(form.lineRef), new_price: Number(form.newPrice) };
    } else if (respondType === "substitution_proposed") {
      if (form.lineRef === "" || !form.proposedItem) return toast.error("Select a line and describe the substitution");
      details = { line_ref: Number(form.lineRef), proposed_item: form.proposedItem };
    } else if (respondType === "delivery_date_confirmed") {
      if (!form.date) return toast.error("Enter the confirmed date");
      details = { date: form.date };
    } else if (respondType === "shipped") {
      if (!form.trackingNumber) return toast.error("Enter a tracking number");
      details = { tracking_number: form.trackingNumber, carrier: form.carrier || null, expected_delivery_date: form.shipDate || null };
    }
    try {
      await api.post(`/vendor/orders/${id}/respond`, { type: respondType, details, note: form.note || null });
      toast.success("Response sent");
      setShowRespond(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to respond");
    }
  };

  const canRespond = po.status !== "draft";
  const canUpdateDelivery = po.status !== "sent" && po.status !== "delivered" && po.status !== "draft";

  return (
    <div className="p-6 md:p-8 max-w-5xl" data-testid="vendor-order-detail-page">
      <Link to="/vendor/orders" className="text-[12.5px] text-slate-500 flex items-center gap-1 mb-3"><ChevronLeft size={13} /> Back to Orders</Link>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-extrabold tracking-tight mono">{po.po_number}</h1>
          <StatusBadge status={po.status} />
        </div>
        <div className="flex gap-2 flex-wrap">
          {canRespond && (
            <>
              <button onClick={createInvoice} data-testid="create-invoice-button" className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-emerald-500 text-white text-[13px] font-semibold hover:bg-emerald-600">
                <ReceiptText size={14} /> Create Invoice
              </button>
              <button onClick={() => setShowUpload(true)} data-testid="upload-invoice-button" className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-slate-200 text-[13px] font-semibold hover:bg-slate-50">
                <Upload size={14} /> Upload Invoice
              </button>
            </>
          )}
          <button onClick={downloadPdf} data-testid="download-pdf-button" className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-slate-200 text-[13px] font-semibold hover:bg-slate-50">
            <Download size={14} /> Download PDF
          </button>
        </div>
      </div>

      {invoices.length > 0 && (
        <div className="mt-4 rounded-lg bg-emerald-50 border border-emerald-100 px-3.5 py-2.5 text-[12.5px]" data-testid="po-invoices-strip">
          <span className="font-semibold text-emerald-800">Invoices on this order:</span>
          {invoices.map((inv) => (
            <button key={inv.id} onClick={() => navigate(`/vendor/invoices/${inv.id}`)} className="ml-2 mono text-sky-700 font-medium hover:underline">
              {inv.invoice_number} ({inv.status})
            </button>
          ))}
        </div>
      )}

      <div className="mt-5 grid sm:grid-cols-3 gap-3 text-[12.5px]" data-testid="vendor-po-context">
        <div className="border border-slate-200 rounded-lg p-3">
          <div className="flex items-center gap-1.5 text-slate-400 font-semibold uppercase tracking-wide text-[10.5px]"><Briefcase size={12} /> Job</div>
          <div className="mt-1 font-medium text-slate-700" data-testid="po-context-job">{po.job_name || "Not job-scoped"}</div>
        </div>
        <div className="border border-slate-200 rounded-lg p-3">
          <div className="flex items-center gap-1.5 text-slate-400 font-semibold uppercase tracking-wide text-[10.5px]"><MapPin size={12} /> Ship To</div>
          {po.delivery_address ? (
            <div className="mt-1 text-slate-700" data-testid="po-context-address">
              <div>{po.delivery_address.address_line1}{po.delivery_address.address_line2 ? `, ${po.delivery_address.address_line2}` : ""}</div>
              <div>{[po.delivery_address.city, po.delivery_address.state, po.delivery_address.zip].filter(Boolean).join(", ")}</div>
              {po.delivery_address.site_contact_name && <div className="mt-1 text-slate-500">{po.delivery_address.site_contact_name}{po.delivery_address.site_contact_phone ? ` · ${po.delivery_address.site_contact_phone}` : ""}</div>}
              {po.delivery_address.instructions && <div className="mt-1 text-slate-500 italic">{po.delivery_address.instructions}</div>}
            </div>
          ) : <div className="mt-1 font-medium text-slate-700">No address on file</div>}
        </div>
        <div className="border border-slate-200 rounded-lg p-3">
          <div className="flex items-center gap-1.5 text-slate-400 font-semibold uppercase tracking-wide text-[10.5px]"><User size={12} /> Requested By</div>
          <div className="mt-1 font-medium text-slate-700" data-testid="po-context-requester">{po.requester_name || "—"}</div>
        </div>
      </div>

      {po.tracking_number && (
        <div className="mt-4 flex items-center justify-between gap-3 text-[13px] bg-sky-50 border border-sky-100 rounded-lg px-3.5 py-3" data-testid="tracking-banner">
          <div>
            <span className="text-slate-500">Tracking:</span> <span className="font-semibold mono">{po.tracking_number}</span>
            {po.carrier && <span className="text-slate-500"> · {po.carrier}</span>}
          </div>
          <button
            onClick={() => { navigator.clipboard?.writeText(po.tracking_number); toast.success("Tracking number copied"); }}
            data-testid="copy-tracking-button"
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-sky-200 text-[12px] font-semibold hover:bg-sky-100"
          >
            <Copy size={12} /> Copy
          </button>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-8 mt-7">
        <div className="md:col-span-2">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead><tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100"><th className="text-left py-2">#</th><th className="text-left py-2">Description</th><th className="text-right py-2">Qty</th><th className="text-right py-2">Unit Price</th><th className="text-center py-2">Taxable</th><th className="text-right py-2">Total</th></tr></thead>
              <tbody>
                {po.items.map((it, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-2.5 text-slate-400">{i + 1}</td>
                    <td className="py-2.5">{it.description}</td><td className="py-2.5 text-right">{it.qty} {it.uom || it.unit}</td><td className="py-2.5 text-right mono">{fmt(it.unit_price)}</td>
                    <td className="py-2.5 text-center"><input type="checkbox" checked={!!it.taxable} disabled={TAX_LOCKED.includes(po.status)} onChange={(e) => setLineTaxable(i, e.target.checked)} data-testid={`order-line-taxable-${i}`} /></td>
                    <td className="py-2.5 text-right mono font-medium">{fmt(it.extended_price ?? it.qty * it.unit_price)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {po.tax_total > 0 && <tr><td colSpan={5} className="text-right py-1.5 text-slate-500">Tax (job rate)</td><td className="text-right py-1.5 mono">{fmt(po.tax_total)}</td></tr>}
                <tr><td colSpan={5} className="text-right py-3 font-bold">Total</td><td className="text-right py-3 font-bold mono">{fmt(po.amount)}</td></tr>
              </tfoot>
            </table>
          </div>
          {po.payment_method && (
            <div className="text-[12.5px] text-slate-500 mt-2" data-testid="po-payment-basis">
              Payment: <span className="font-semibold text-slate-700">
                {po.payment_method === "card" ? "Paid by card" : po.payment_method === "ramp" ? "Paid by card (Ramp)" : `On account — ${(po.payment_terms_snapshot || "").replace("_", " ") || "terms not specified"}`}
              </span>
            </div>
          )}

          {canRespond && (
            <div className="mt-6">
              <div className="text-[13px] font-semibold text-slate-700 mb-2">Respond to this Order</div>
              <div className="flex flex-wrap gap-2">
                {RESPONSE_TYPES.map((t) => (
                  <button key={t.value} onClick={() => openRespond(t.value)} data-testid={`respond-${t.value}`} className="px-3 py-2 rounded-lg border border-slate-200 text-[12.5px] font-semibold hover:bg-slate-50">
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {canUpdateDelivery && (
            <div className="mt-5 flex gap-2">
              {DELIVERY_STATUSES.map((s) => (
                <button key={s.value} onClick={() => updateStatus(s.value)} data-testid={`update-status-${s.value}`} className="px-3 py-2 rounded-lg border border-slate-200 text-[12.5px] font-semibold hover:bg-slate-50">{s.label}</button>
              ))}
            </div>
          )}

          <div className="mt-8">
            <div className="text-[14.5px] font-bold mb-3">Response History</div>
            <div className="space-y-2.5" data-testid="vendor-response-timeline">
              {(po.responses || []).length === 0 && <div className="text-slate-400 text-[13px]">No responses recorded yet.</div>}
              {(po.responses || []).slice().reverse().map((r) => {
                const meta = responseTypeMeta(r.type);
                const Icon = meta.icon;
                return (
                  <div key={r.id} className="border border-slate-200 rounded-xl p-3" data-testid={`vendor-response-row-${r.id}`}>
                    <div className="flex items-center gap-2">
                      <Icon size={15} className={meta.color} />
                      <span className="font-semibold text-[13px]">{meta.label}</span>
                      <span className="text-[11px] text-slate-400">· {fmtDateTime(r.created_at)}</span>
                    </div>
                    {r.type === "shipped" && (
                      <div className="text-[12.5px] text-slate-600 mt-1.5">
                        {r.details.tracking_number}{r.details.carrier ? ` · ${r.details.carrier}` : ""}{r.details.expected_delivery_date ? ` · ETA ${fmtDate(r.details.expected_delivery_date)}` : ""}
                      </div>
                    )}
                    {r.note && <div className="text-[12.5px] text-slate-500 mt-1 italic">"{r.note}"</div>}
                  </div>
                );
              })}
            </div>
          </div>

          <DocumentsPanel entityType="purchase_order" entityId={po.id} />
        </div>
        <div>
          <ChatPanel contractorTenantId={po.tenant_id} vendorId={user?.tenant_id} subjectType="po" subjectId={po.id} subjectLabel={po.po_number} />
        </div>
      </div>

      <Dialog open={showRespond} onOpenChange={setShowRespond}>
        <DialogContent data-testid="respond-modal">
          <DialogHeader><DialogTitle>{responseTypeMeta(respondType).label}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            {respondType === "backordered" && (
              <div>
                <label className="text-[13px] font-medium">Backordered Line(s)</label>
                <div className="mt-1.5 space-y-1 max-h-32 overflow-y-auto ss-scrollbar">
                  {po.items.map((it, i) => (
                    <label key={i} className="flex items-center gap-2 text-[12.5px]">
                      <input type="checkbox" checked={form.lineRefs.includes(String(i))} onChange={() => toggleLineRef(i)} data-testid={`respond-backorder-line-${i}`} />
                      {it.description}
                    </label>
                  ))}
                </div>
                <input type="date" value={form.estimatedDate} onChange={(e) => setForm({ ...form, estimatedDate: e.target.value })} data-testid="respond-backorder-eta-input" className="mt-2 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
              </div>
            )}
            {respondType === "price_changed" && (
              <div className="grid grid-cols-2 gap-2">
                <SearchableSelect value={form.lineRef} onChange={(e) => setForm({ ...form, lineRef: e.target.value })} data-testid="respond-price-line-select" className="h-10 px-3 rounded-lg border border-slate-200 text-sm">
                  <option value="">Line…</option>
                  {po.items.map((it, i) => <option key={i} value={i}>{i + 1}. {it.description}</option>)}
                </SearchableSelect>
                <input type="number" min="0" step="0.01" value={form.newPrice} onChange={(e) => setForm({ ...form, newPrice: e.target.value })} placeholder="New unit price" data-testid="respond-price-value-input" className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
              </div>
            )}
            {respondType === "substitution_proposed" && (
              <div className="space-y-2">
                <SearchableSelect value={form.lineRef} onChange={(e) => setForm({ ...form, lineRef: e.target.value })} data-testid="respond-substitution-line-select" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm">
                  <option value="">Line…</option>
                  {po.items.map((it, i) => <option key={i} value={i}>{i + 1}. {it.description}</option>)}
                </SearchableSelect>
                <input value={form.proposedItem} onChange={(e) => setForm({ ...form, proposedItem: e.target.value })} placeholder="Proposed substitute item" data-testid="respond-substitution-item-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
              </div>
            )}
            {respondType === "delivery_date_confirmed" && (
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="respond-delivery-date-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            )}
            {respondType === "shipped" && (
              <div className="space-y-2">
                <input value={form.trackingNumber} onChange={(e) => setForm({ ...form, trackingNumber: e.target.value })} placeholder="Tracking number" data-testid="respond-tracking-number-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                <div className="grid grid-cols-2 gap-2">
                  <input value={form.carrier} onChange={(e) => setForm({ ...form, carrier: e.target.value })} placeholder="Carrier (optional)" data-testid="respond-carrier-input" className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                  <input type="date" value={form.shipDate} onChange={(e) => setForm({ ...form, shipDate: e.target.value })} data-testid="respond-ship-eta-input" className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                </div>
              </div>
            )}
            <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Note (optional)" rows={2} data-testid="respond-note-input" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            <button onClick={submitRespond} data-testid="confirm-respond-button" className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold">Send Response</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent data-testid="upload-invoice-modal">
          <DialogHeader><DialogTitle>Upload Invoice for {po.po_number}</DialogTitle></DialogHeader>
          <div className="space-y-2.5">
            <p className="text-[12.5px] text-slate-500">Attach the invoice your own accounting or POS system produced. It's sent to the contractor immediately.</p>
            <div className="grid grid-cols-2 gap-2">
              <input value={upload.invoiceNumber} onChange={(e) => setUpload({ ...upload, invoiceNumber: e.target.value })} placeholder="Invoice number *" data-testid="upload-invoice-number-input" className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
              <input type="number" min="0" step="0.01" value={upload.total} onChange={(e) => setUpload({ ...upload, total: e.target.value })} placeholder="Total amount *" data-testid="upload-invoice-total-input" className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] text-slate-400 font-semibold uppercase">Invoice date</label>
                <input type="date" value={upload.invoiceDate} onChange={(e) => setUpload({ ...upload, invoiceDate: e.target.value })} data-testid="upload-invoice-date-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
              </div>
              <div>
                <label className="text-[11px] text-slate-400 font-semibold uppercase">Due date</label>
                <input type="date" value={upload.dueDate} onChange={(e) => setUpload({ ...upload, dueDate: e.target.value })} data-testid="upload-invoice-due-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
              </div>
            </div>
            <input value={upload.payLink} onChange={(e) => setUpload({ ...upload, payLink: e.target.value })} placeholder="Payment link (optional — your merchant account)" data-testid="upload-invoice-paylink-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={(e) => setUpload({ ...upload, file: e.target.files?.[0] || null })} data-testid="upload-invoice-file-input" className="w-full text-[13px]" />
            <textarea value={upload.notes} onChange={(e) => setUpload({ ...upload, notes: e.target.value })} placeholder="Notes (optional)" rows={2} data-testid="upload-invoice-notes-input" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            <button onClick={submitUpload} data-testid="confirm-upload-invoice-button" className="w-full h-10 rounded-lg bg-emerald-500 text-white text-[13px] font-semibold">Upload & Send Invoice</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
